import { Select, TextInput } from '@octano/global-ui';
import { Fragment, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Col, Row } from 'reactstrap';
import SectionTitle from '../../../Text/SectionTitle';

type ContactSectionProps = {
  prefix: string;
  fieldNames?: {
    businessAddressStreet: string;
    businessAddressNumber: string;
    businessAddressExtra: string;
    businessCountry: string;
    businessRegion: string;
    businessCommune: string;
    businessPhone: string;
    businessCellPhone: string;
    businessEmail: string;
    businessLocation: string;
  };
  disabled?: boolean;
  hasEmail?: boolean;
};

/**
 * Para usar este componente tu formulario debe estar envuelto en su FormProvider
 * para poder acceder a la información del formulario a través de useFormContext
 * Los nombres agregados en fieldNames se relacionan a los campos que utilizaste
 * al definir tu formulario.
 */
const DynamicContactCampusBusiness = ({
  prefix,
  fieldNames = {
    businessAddressStreet: 'businessAddressStreet',
    businessAddressNumber: 'businessAddressNumber',
    businessAddressExtra: 'businessAddressExtra',
    businessCountry: 'businessCountry',
    businessRegion: 'businessRegion',
    businessCommune: 'businessCommune',
    businessPhone: 'businessPhone',
    businessCellPhone: 'businessCellPhone',
    businessEmail: 'businessEmail',
    businessLocation: 'businessLocation',
  },
  disabled = false,
  hasEmail = false,
}: ContactSectionProps) => {
  const { t } = useTranslation();
  const { control } = useFormContext();

  const fields = useMemo(() => {
    return (
      <Fragment>
        <Row className="pt-5">
          <Col xs={12}>
            <SectionTitle text={t(`${prefix}.title`)} />
          </Col>
        </Row>
        <Row>
          <Col className="pb-3" xs={12} lg={8}>
            <TextInput
              name={fieldNames.businessAddressStreet}
              label={t(`${prefix}.${fieldNames.businessAddressStreet}`)}
              control={control}
              readOnly={disabled}
            />
          </Col>
          <Col className="pb-3" xs={12} lg={4}>
            <TextInput
              name={fieldNames.businessAddressNumber}
              label={t(`${prefix}.${fieldNames.businessAddressNumber}`)}
              control={control}
            />
          </Col>
          <Col className="pb-3" xs={12} lg={4}>
            <TextInput
              name={fieldNames.businessAddressExtra}
              label={t(`${prefix}.${fieldNames.businessAddressExtra}`)}
              control={control}
            />
          </Col>

          <Col className="pb-3" xs={12} lg={4}>
            <Select
              name={fieldNames.businessRegion}
              label={t(`${prefix}.${fieldNames.businessRegion}`)}
              options={[]}
              control={control}
            />
          </Col>
          <Col className="pb-3" xs={12} lg={4}>
            <Select
              name={fieldNames.businessCommune}
              label={t(`${prefix}.${fieldNames.businessCommune}`)}
              options={[]}
              control={control}
              shouldUnregister
            />
          </Col>

          <Col className="pb-3" xs={12} lg={4}>
            <TextInput
              name={fieldNames.businessPhone}
              label={t(`${prefix}.${fieldNames.businessPhone}`)}
              control={control}
              formatter="phone"
              placeholder={t(`common.placeholder.phone`)}
            />
          </Col>
          <Col className="pb-3" xs={12} lg={4}>
            <TextInput
              name={fieldNames.businessCellPhone}
              label={t(`${prefix}.${fieldNames.businessCellPhone}`)}
              control={control}
              formatter="phone"
              placeholder={t(`common.placeholder.cellphone`)}
            />
          </Col>
          {hasEmail ? (
            <Col className="pb-3" xs={12} lg={4}>
              <TextInput
                name={fieldNames.businessEmail}
                label={t(`${prefix}.${fieldNames.businessEmail}`)}
                control={control}
                disabled={disabled}
              />
            </Col>
          ) : null}
        </Row>
      </Fragment>
    );
  }, [
    disabled,
    fieldNames.businessAddressStreet,
    fieldNames.businessAddressNumber,
    fieldNames.businessAddressExtra,
    fieldNames.businessRegion,
    fieldNames.businessCommune,
    fieldNames.businessPhone,
    fieldNames.businessCellPhone,
    fieldNames.businessEmail,
    hasEmail,
    t,
    control,
    prefix,
  ]);

  return <>{fields}</>;
};

export default DynamicContactCampusBusiness;
